import { createSelector } from '@reduxjs/toolkit';

import { Role } from 'types';

import { RootState } from '../../store';

import { types } from './types';
import { login, logout } from './thunks';

const authStore = (store: RootState) => store?.auth;

const selectIsLoggedIn = createSelector(
  [authStore],
  (store) => store?.isLoggedIn ?? false
);

const selectAuthError = createSelector([authStore], (store) => store?.error);

const selectAuthRestorePath = createSelector(
  [authStore],
  (store) => store?.path
);

const selectAuthUser = createSelector([authStore], (store) => store?.user);

// "Full" here means "uses the project/tenant-picker report flow scoped to
// whatever the backend returns" (project- and tenant-level reports across
// multiple organizations), not "is an admin". Role.LHD_MIVAX_ADMIN is
// project-scoped (backend restricts the project/tenant/report data to
// their single assigned project and Health Departments), so it belongs here
// even though it has no other admin-like frontend capability.
const selectHasFullReportAccess = createSelector([selectAuthUser], (user) => {
  return (
    user?.role === Role.ADMIN ||
    user?.role === Role.PROJECT_ADMIN ||
    user?.role === Role.RESEARCH_ADMIN ||
    user?.role === Role.LHD_MIVAX_ADMIN
  );
});

const selectCanSelectProjectLevel = createSelector(
  [selectAuthUser, selectHasFullReportAccess],
  (user, hasFullReportAccess) =>
    hasFullReportAccess && user?.role !== Role.LHD_MIVAX_ADMIN
);

const selectCanAccessReportIdentity = createSelector(
  [selectAuthUser],
  (user) =>
    user?.role === Role.ADMIN ||
    user?.role === Role.PROJECT_ADMIN ||
    user?.role === Role.LHD_MIVAX_ADMIN
);

const selectCanEditReportUser = createSelector(
  [selectAuthUser],
  (user) =>
    user?.role === Role.ADMIN ||
    user?.role === Role.TENANT_ADMIN ||
    user?.role === Role.LHD_MIVAX_ADMIN
);

const selectCanEditReportChild = createSelector(
  [selectAuthUser],
  (user) => user?.role === Role.TENANT_ADMIN
);

const selectCanEditAdditionalInfo = createSelector(
  [selectAuthUser],
  (user) => user?.role === Role.ADMIN || user?.role === Role.LHD_MIVAX_ADMIN
);

const selectCanManageReportTemplates = createSelector(
  [selectAuthUser],
  (user) =>
    user?.role === Role.ADMIN ||
    user?.role === Role.PROJECT_ADMIN ||
    user?.role === Role.TENANT_ADMIN ||
    user?.role === Role.RESEARCH_ADMIN ||
    user?.role === Role.LHD_MIVAX_ADMIN
);

export {
  login,
  types,
  logout,
  selectAuthUser,
  selectAuthError,
  selectIsLoggedIn,
  selectAuthRestorePath,
  selectCanEditReportUser,
  selectCanEditReportChild,
  selectHasFullReportAccess,
  selectCanEditAdditionalInfo,
  selectCanSelectProjectLevel,
  selectCanAccessReportIdentity,
  selectCanManageReportTemplates,
};
